import * as React from 'react'
import { fromJSX } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { linkWrapper } from '../../../utils/link'
import { LinkGridBlockProps, LinkGridItemKindProps, LinkGridItemProps } from './gridBlock.state'
import { linkGridBlockClasses, LinkGridBlockLayout } from './gridBlock.layout'
import { dummyData, randomCaption, randomTitle, randomWords } from '../../../utils/dummyData'
import { DP } from '../../../drupal/blockDefinitions'
import { parseDrupalImageUrls } from '../../../drupal/utils/modernImage'
import { linkGridSrc, linkGridSrcSet } from '../../../drupal/utils/imageSizes'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'

export const linkGridBlock: ModernBlock<LinkGridBlockProps, any, any, DP.GridImage | DP.GridPlain> =
  {
    kind: 'linkGridBlock',
    render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
      fromJSX((updateBlock) => (
        <LinkGridBlockLayout.Content
          title={blockProps.title!}
          label={blockProps.label!}
          intro={blockProps.intro!}
          items={blockProps.items}
          showPrimary={blockProps.showPrimary}
          showSecondary={blockProps.showSecondary}
          primaryAction={blockProps.primaryAction}
          secondaryAction={blockProps.secondaryAction}
        />
      )),
    adapters: {
      drupal: (dpBlock: DP.GridImage | DP.GridPlain, blockIndex: number) => {
        const targetId = dpBlock.target.type[0].target_id
        if (targetId != 'grid_blocks_plain' && targetId != 'grid_blocks_image') return undefined


        const mapGridItem = (block: (DP.GridPlain | DP.GridImage)["target"]["field_grid_blocks"][0]): LinkGridItemProps => {
          const { field_link } = block.target
          const getKind = (): LinkGridItemKindProps => {
            if (block.target.field_download && block.target.field_download[0]) return "download"
            if (field_link[0]?.processed_url) return "link"

            return "none"
          }

          const getLink = (itemKind: LinkGridItemKindProps) => {
            if (itemKind == "link") {
              return linkWrapper({
                title: field_link[0]?.title || 'Read more',
                url: field_link[0]?.processed_url,
                anchor: field_link[0]?.options?.attributes?.id
              })
            }
            if (itemKind == "download") {
              return linkWrapper({
                title: block.target.field_download_label ? block.target.field_download_label[0]?.value : "download" || "download",
                url: block.target.field_download[0].target.field_media_file[0].target.uri[0].url
              })
            }

            return undefined
          }

          const kind = getKind()

          return {
            kind: kind,
            label: block.target.field_label[0]?.value,
            title: block.target.field_title[0]?.value,
            intro: block.target.field_introduction[0]?.value,
            icon: undefined, // TODO make dynamic
            link: getLink(kind),
          }
        }

        const mapGridImageItem = (block: DP.GridImage["target"]["field_grid_blocks"][0]): LinkGridItemProps => {
          let item = mapGridItem(block)
          const fieldMediaImage = block.target.field_image[0].target.field_media_image[0]
          const fieldMediaSource = block.target.field_image[0]?.target.field_source[0]
            ? block.target.field_image[0]?.target.field_source
            : undefined
          const dpImages = fieldMediaImage
            ? parseDrupalImageUrls(fieldMediaImage, fieldMediaSource, linkGridSrcSet, linkGridSrc)?.image
            : undefined
          item.image = dpImages

          return item
        }


        return {
          index: blockIndex,
          title: dpBlock.target.field_title[0]?.value,
          excludeFromPageMenu: dpBlock.target?.field_exclude_from_page_menu?.[0]?.value,
          label: dpBlock.target.field_label[0]?.value,
          intro: dpBlock.target.field_introduction[0]?.value,
          showPrimary: dpBlock.target?.field_links[0] ? true : false,
          showSecondary: dpBlock.target?.field_links[1] ? true : false,
          primaryAction: linkWrapper({
            title: dpBlock.target?.field_links[0]?.title!,
            url: dpBlock.target?.field_links[0]?.processed_url!,
            anchor: dpBlock.target?.field_links[0]?.options?.attributes?.id!,
          }),
          secondaryAction: linkWrapper({
            title: dpBlock.target?.field_links[1]?.title!,
            url: dpBlock.target?.field_links[1]?.processed_url!,
            anchor: dpBlock.target?.field_links[1]?.options?.attributes?.id!,
          }),
          items: dpBlock.target.field_grid_blocks.map((block: (DP.GridImage | DP.GridPlain)["target"]["field_grid_blocks"][0]): LinkGridItemProps => {
            if (targetId == "grid_blocks_plain") return mapGridItem(block as DP.GridPlain["target"]["field_grid_blocks"][0])
            if (targetId == "grid_blocks_image") return mapGridImageItem(block as DP.GridImage["target"]["field_grid_blocks"][0])

            return {} as LinkGridItemProps // base case to make ts happy (this will never happen)
          }),
          anchorTitle: dpBlock.target.field_title[0]
            ? slugify(dpBlock.target.field_title[0]?.value)
            : `${targetId}-title-${blockIndex}`,
        }
      },
    },
    seoEvents: (props, blockIndex) =>
      [
        ...props.items.map((i, index) => ({
          id: `grid_item_${index}`,
          event: 'onClick',
          category: 'gridblock' + props.items[0]?.image ? '_with_image' : '',
          label: window.location.pathname,
          action: 'grid_item_' + i.title,
        })),
        props.primaryAction
          ? {
              id: `primary_button`,
              event: 'onClick',
              category: 'gridblock' + props.items[0]?.image ? '_with_image' : '',
              label: window.location.pathname,
              action: 'primary_' + props.primaryAction.title,
            }
          : undefined,
        props.secondaryAction
          ? {
              id: `secondary_button`,
              event: 'onClick',
              category: 'gridblock' + props.items[0]?.image ? '_with_image' : '',
              label: window.location.pathname,
              action: 'secondary_' + props.secondaryAction.title,
            }
          : undefined,
      ].filter((i) => i != undefined) as SeoEvent[],

    inView: {
      threshold: 0.75,
      triggerOnce: false,
      inViewClassName: 'inview',
      showAs: 'section',
      updateState: (currentInviewState, currentBlockState) => {
        return {
          ...currentBlockState,
          inviewState: currentInviewState,
        }
      },
    },
    blockClassName: (props) => `
    ${getClassNameModern(linkGridBlockClasses, 'block', 'mb-6 py-6')}
    ${getClassNameModern(
      linkGridBlockClasses,
      props.items[0]?.icon ? 'blockIcon' : props.items[0]?.image ? 'blockImage' : 'blockText',
      'mb-6 bg-secondary text-white py-6'
    )}`,
    sampleExample: [
      {
        index: 10,
        label: randomWords(),
        title: randomTitle(),
        intro: dummyData.intro,
        anchorTitle: '',
        showPrimary: true,
        showSecondary: true,
        primaryAction: linkWrapper({ title: randomWords(), url: 'https://www.hoppinger.com' }),
        secondaryAction: linkWrapper({ title: randomWords(), url: '' }),
        items: [
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            link: linkWrapper({ title: randomWords(), url: '' }),
          },
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            link: linkWrapper({ title: randomWords(), url: 'https://www.hoppinger.com' }),
          },
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            link: linkWrapper({ title: randomWords(), url: 'https://www.hoppinger.com' }),
          },
        ],
      },
      {
        index: 11,
        label: randomWords(),
        title: randomTitle(),
        intro: dummyData.intro,
        anchorTitle: '',
        showPrimary: false,
        showSecondary: false,
        items: [
          {
            kind: 'none',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
          },
          {
            kind: 'none',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
          },
          {
            kind: 'none',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
          },
        ],
      },
      {
        index: 12,
        label: randomWords(),
        title: randomTitle(),
        intro: dummyData.intro,
        anchorTitle: '',
        showPrimary: true,
        showSecondary: true,
        primaryAction: linkWrapper({ title: randomWords(), url: 'https://www.viddadigital.com' }),
        secondaryAction: linkWrapper({ title: randomWords(), url: '' }),
        items: [
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            link: linkWrapper({ title: randomWords(), url: '' }),
            image: dummyData.image,
          },
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            link: linkWrapper({ title: randomWords(), url: 'https://www.hoppinger.com' }),
            image: dummyData.image,
          },
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            link: linkWrapper({ title: randomWords(), url: '' }),
            image: dummyData.image,
          },
        ],
      },
      {
        index: 13,
        label: randomWords(),
        title: randomTitle(),
        intro: dummyData.intro,
        anchorTitle: '',
        showPrimary: false,
        showSecondary: false,
        items: [
          {
            kind: 'none',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            image: dummyData.image,
          },
          {
            kind: 'none',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            image: dummyData.image,
          },
          {
            kind: 'none',
            intro: randomCaption(),
            title: randomTitle(),
            label: randomWords(),
            image: dummyData.image,
          },
        ],
      },
      {
        index: 14,
        anchorTitle: '',
        showPrimary: false,
        showSecondary: false,
        items: [
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            link: linkWrapper({ title: randomWords(), url: '' }),
            image: dummyData.image,
          },
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            link: linkWrapper({ title: randomWords(), url: 'https://www.hoppinger.com' }),
            image: dummyData.image,
          },
          {
            kind: 'link',
            intro: randomCaption(),
            title: randomTitle(),
            link: linkWrapper({ title: randomWords(), url: '' }),
            image: dummyData.image,
          },
        ],
      },
    ],
  }
